import { useState, useEffect, type ReactNode } from 'react';
import { api } from '../api/client';
import type { BillRunDashboard as DashboardData } from '../api/types';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { Card, Alert, DataTable } from './ui';
import type { DataTableColumn } from './ui';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { fmtCurrency, fmtNum } from '../lib/fmt';

const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16'];

// Shared shape for the elec/water aggregate-row strips below, each a
// dl>dt/dd equivalent of DataTable.tsx's own collapsed-row label/value list
// (DataTable.tsx:172-186) instead of the plain <span>Label: <strong>Value
// </strong></span> pairs these used to be — same association a screen
// reader gets from a real definition list, at zero visual cost (still flex
// items, same classes as before).
function TotalsStrip({ children }: { children: ReactNode }) {
  return (
    <dl className="px-4 py-2 mt-2 border-t border-border-token bg-surface-sunken flex flex-wrap items-center gap-x-4 gap-y-1 text-sm rounded">
      {children}
    </dl>
  );
}

function TotalsItem({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex items-baseline gap-1 text-content-muted">
      <dt>{label}:</dt>
      <dd><strong className="text-content font-mono">{value}</strong></dd>
    </div>
  );
}

interface Props {
  billRunId: string;
}

export default function BillRunDashboard({ billRunId }: Props) {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [comparing, setComparing] = useState(false);
  // Tailwind `xl`, not the app-wide `lg` useIsDesktop() switch: at lg the
  // charts grid turns two-column while the w-64 aside is already showing, so
  // 1024px gives this Card the same ~290px it has on a phone. See the Charge
  // Breakdown block below for the arithmetic and the recharts sizing rule.
  const isWideChart = useMediaQuery('(min-width: 1280px)');

  useEffect(() => {
    loadDashboard();
  }, [billRunId]);

  const loadDashboard = async () => {
    try {
      setLoading(true);
      const res = await api.getBillRunDashboard(billRunId);
      setData(res.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleCompare = async () => {
    try {
      setComparing(true);
      await api.compareBillRun(billRunId);
      await loadDashboard();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Comparison failed');
    } finally {
      setComparing(false);
    }
  };

  // Converged onto src/lib/fmt.ts — the module these two were originally
  // extracted INTO for Insights, while this copy stayed behind. See the
  // non-breaking-separator note there.

  if (loading) {
    return (
      <div className="animate-pulse space-y-4 mb-8">
        <div className="h-20 bg-border-token rounded-xl"></div>
        <div className="grid grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => <div key={i} className="h-24 bg-border-token rounded-xl"></div>)}
        </div>
      </div>
    );
  }

  if (error || !data) {
    return error ? (
      <Alert tone="danger" className="mb-8">
        {error}
      </Alert>
    ) : null;
  }

  const touTotal = data.tou.peak_kwh + data.tou.standard_kwh + data.tou.off_peak_kwh;
  const hasTou = touTotal > 0;

  // Split meters by type. `_key` is a synthetic id appended for DataTable's
  // rowKey — these report rows have no natural unique id (the original table
  // keyed off the raw array index) — order is unaffected, filter then map.
  const elecMeters = data.meters
    .filter(m => m.meter_type === 'electricity')
    .map((m, i) => ({ ...m, _key: `elec-${i}` }));
  const waterMeters = data.meters
    .filter(m => m.meter_type === 'water')
    .map((m, i) => ({ ...m, _key: `water-${i}` }));
  type ElecRow = (typeof elecMeters)[number];
  type WaterRow = (typeof waterMeters)[number];

  // Electricity table: no selection, no rowActions — only DataTable's own
  // expand toggle is added on mobile, so there's headroom for 3 primary
  // columns (identifying Meter + the two numbers people scan for: current
  // consumption and cost). Previous kWh/Variance/Max kVA/CA kWh are one tap
  // away. Array order matches the original table's left-to-right header
  // order — priority is set per column, not by regrouping the array.
  const elecColumns: DataTableColumn<ElecRow>[] = [
    { key: 'meter', label: 'Meter', priority: 'primary', render: (m) => <span className="font-medium">{m.meter_name}</span> },
    { key: 'current_kwh', label: 'Current kWh', priority: 'primary', numeric: true, render: (m) => <span className="font-mono">{fmtNum(m.delta_kwh, 1)}</span> },
    ...(data.comparison_available ? [
      {
        key: 'previous_kwh', label: 'Previous kWh', priority: 'secondary' as const, numeric: true,
        render: (m: ElecRow) => <span className="font-mono">{m.prev_delta !== null ? fmtNum(m.prev_delta, 1) : '-'}</span>,
      },
      {
        key: 'variance', label: 'Variance', priority: 'secondary' as const, numeric: true,
        // Recomputed from hex against the Card's dark surface
        // (surfaceRaised dark, #292524): text-red-600 is 3.14:1 (fails AA,
        // as this comment previously claimed for both at "2.93:1") but
        // text-green-600 actually clears it at 4.60:1. dark:-400 clears both
        // regardless (5.48:1 / 8.71:1) — unchanged from the pre-migration
        // cell, just relocated into this render fn.
        render: (m: ElecRow) => m.variance_percent !== null ? (
          <span className={m.variance_percent > 10 ? 'text-red-600 dark:text-red-400' : m.variance_percent < -10 ? 'text-green-600 dark:text-green-400' : 'text-content-muted'}>
            {m.variance_percent > 0 ? '+' : ''}{fmtNum(m.variance_percent, 1)}%
          </span>
        ) : '-',
      },
    ] : []),
    { key: 'max_kva', label: 'Max kVA', priority: 'secondary', numeric: true, render: (m) => <span className="font-mono">{fmtNum(m.max_kva, 1)}</span> },
    { key: 'ca_kwh', label: 'CA kWh', priority: 'secondary', numeric: true, render: (m) => <span className="font-mono">{m.common_area_kwh > 0 ? fmtNum(m.common_area_kwh, 1) : '-'}</span> },
    { key: 'amount', label: 'Amount', priority: 'primary', numeric: true, render: (m) => <span className="font-mono font-semibold">{fmtCurrency(m.total_amount_cents)}</span> },
  ];

  // Same reasoning as elecColumns, minus the kVA/CA-kWh columns water meters
  // don't have.
  const waterColumns: DataTableColumn<WaterRow>[] = [
    { key: 'meter', label: 'Meter', priority: 'primary', render: (m) => <span className="font-medium">{m.meter_name}</span> },
    { key: 'current_kl', label: 'Current kL', priority: 'primary', numeric: true, render: (m) => <span className="font-mono">{fmtNum(m.delta_kl, 1)}</span> },
    ...(data.comparison_available ? [
      {
        key: 'previous_kl', label: 'Previous kL', priority: 'secondary' as const, numeric: true,
        render: (m: WaterRow) => <span className="font-mono">{m.prev_delta !== null ? fmtNum(m.prev_delta, 1) : '-'}</span>,
      },
      {
        key: 'variance', label: 'Variance', priority: 'secondary' as const, numeric: true,
        // Same pre-existing AA computation as elecColumns' identical cell.
        render: (m: WaterRow) => m.variance_percent !== null ? (
          <span className={m.variance_percent > 10 ? 'text-red-600 dark:text-red-400' : m.variance_percent < -10 ? 'text-green-600 dark:text-green-400' : 'text-content-muted'}>
            {m.variance_percent > 0 ? '+' : ''}{fmtNum(m.variance_percent, 1)}%
          </span>
        ) : '-',
      },
    ] : []),
    { key: 'amount', label: 'Amount', priority: 'primary', numeric: true, render: (m) => <span className="font-mono font-semibold">{fmtCurrency(m.total_amount_cents)}</span> },
  ];

  // Chart data
  const barData = data.meters.map(m => ({
    name: m.meter_name.length > 15 ? m.meter_name.substring(0, 15) + '...' : m.meter_name,
    current: Math.round(m.delta_kwh),
    previous: m.prev_delta ? Math.round(m.prev_delta) : 0,
  }));

  const pieData = data.charges.filter(c => c.total_cents > 0).map(c => ({
    name: c.description.length > 25 ? c.description.substring(0, 25) + '...' : c.description,
    value: c.total_cents / 100,
  }));
  // Only consumed by the narrow-viewport list, to state each slice's share as
  // a number instead of an angle. Same denominator the donut divides by
  // (Pie sums `dataKey` over its own data), so the percentages match it.
  const pieTotal = pieData.reduce((sum, d) => sum + d.value, 0);

  return (
    <div className="mb-8 space-y-6">
      {/* Header Banner */}
      <div className="bg-gradient-to-r from-slate-800 to-slate-700 rounded-xl p-6 text-white">
        <p className="text-xs uppercase tracking-wider text-slate-400 mb-1">Utility Consumption Report</p>
        <h2 className="text-xl font-bold">{data.building_name || 'Bill Run Report'}</h2>
        <p className="text-slate-300 text-sm mt-1">
          {data.pocket_name && <span>{data.pocket_name} &mdash; </span>}
          {data.period_start} to {data.period_end} &mdash; {data.billing_days} days
        </p>
      </div>

      {/* Summary Tiles */}
      {/* KPI numbers/labels below keep their own accent hues (blue/purple/
          orange/green/red/amber) rather than routing through Badge — these
          are headline stat values, not status chips. dark: added because
          they now sit inside <Card> (bg-surface-raised, dark #292524):
          blue-600, purple-600 and orange-600 all fail AA there (2.93:1,
          2.82:1, 4.26:1) and needed a genuine fix, not just normalization;
          yellow-600 already cleared AA there (5.16:1) but got a matching
          dark: for consistency with its red/green TOU-split siblings.
          -400 shades clear AA against surfaceRaised(dark) in every case
          (blue 5.97, purple 5.74, orange 6.70, yellow 9.91, red 5.48,
          green 8.71). */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="p-5">
          <p className="text-xs uppercase tracking-wider text-content-muted mb-1">Total Electricity</p>
          <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{fmtNum(data.total_kwh)} kWh</p>
          <p className="text-xs text-content-muted mt-1">{data.meter_count} meter{data.meter_count !== 1 ? 's' : ''}</p>
        </Card>
        {hasTou ? (
          <Card className="p-5">
            <p className="text-xs uppercase tracking-wider text-content-muted mb-1">TOU Split</p>
            <div className="space-y-1 mt-2">
              <div className="flex justify-between text-xs">
                <span className="text-red-600 dark:text-red-400">Peak</span>
                <span className="font-medium">{fmtNum(data.tou.peak_kwh)} kWh</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-yellow-600 dark:text-yellow-400">Standard</span>
                <span className="font-medium">{fmtNum(data.tou.standard_kwh)} kWh</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-green-600 dark:text-green-400">Off-Peak</span>
                <span className="font-medium">{fmtNum(data.tou.off_peak_kwh)} kWh</span>
              </div>
            </div>
          </Card>
        ) : (
          <Card className="p-5">
            <p className="text-xs uppercase tracking-wider text-content-muted mb-1">Max Demand</p>
            <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">{fmtNum(data.tou.max_demand_kva, 1)} kVA</p>
          </Card>
        )}
        {data.total_kl > 0 && (
          <Card className="p-5">
            <p className="text-xs uppercase tracking-wider text-content-muted mb-1">Water Consumption</p>
            <p className="text-2xl font-bold text-green-600 dark:text-green-400">{fmtNum(data.total_kl)} kL</p>
          </Card>
        )}
        <Card className="p-5">
          <p className="text-xs uppercase tracking-wider text-content-muted mb-1">Total Amount</p>
          <p className="text-2xl font-bold text-orange-600 dark:text-orange-400">{fmtCurrency(data.total_amount_cents)}</p>
          <p className="text-xs text-content-muted mt-1">Incl. VAT</p>
        </Card>
      </div>

      {/* TOU Breakdown Table */}
      {hasTou && (
        <Card className="p-6">
          <h3 className="text-sm font-semibold text-content mb-4">Time of Use Breakdown</h3>
          <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-surface-sunken">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-content-muted uppercase">Component</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-content-muted uppercase">Consumption</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-content-muted uppercase">% of Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border-token">
              <tr>
                <td className="px-4 py-2">Peak Energy</td>
                <td className="px-4 py-2 text-right font-mono">{fmtNum(data.tou.peak_kwh)} kWh</td>
                <td className="px-4 py-2 text-right font-mono">{touTotal > 0 ? fmtNum((data.tou.peak_kwh / touTotal) * 100, 1) : 0}%</td>
              </tr>
              <tr>
                <td className="px-4 py-2">Standard Energy</td>
                <td className="px-4 py-2 text-right font-mono">{fmtNum(data.tou.standard_kwh)} kWh</td>
                <td className="px-4 py-2 text-right font-mono">{touTotal > 0 ? fmtNum((data.tou.standard_kwh / touTotal) * 100, 1) : 0}%</td>
              </tr>
              <tr>
                <td className="px-4 py-2">Off-Peak Energy</td>
                <td className="px-4 py-2 text-right font-mono">{fmtNum(data.tou.off_peak_kwh)} kWh</td>
                <td className="px-4 py-2 text-right font-mono">{touTotal > 0 ? fmtNum((data.tou.off_peak_kwh / touTotal) * 100, 1) : 0}%</td>
              </tr>
              {data.tou.max_demand_kva > 0 && (
                <tr>
                  <td className="px-4 py-2">Maximum Demand</td>
                  <td className="px-4 py-2 text-right font-mono">{fmtNum(data.tou.max_demand_kva, 1)} kVA</td>
                  <td className="px-4 py-2 text-right font-mono">&mdash;</td>
                </tr>
              )}
              <tr className="font-semibold bg-surface-sunken">
                <td className="px-4 py-2">Total</td>
                <td className="px-4 py-2 text-right font-mono">{fmtNum(touTotal)} kWh</td>
                <td className="px-4 py-2 text-right font-mono">100%</td>
              </tr>
            </tbody>
          </table>
          </div>
        </Card>
      )}

      {/* Compare button (shared across sections) */}
      {!data.comparison_available && (
        <div className="flex justify-end">
          <button
            onClick={handleCompare}
            disabled={comparing}
            className="px-3 py-1.5 text-xs bg-blue-50 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-800 transition-colors disabled:opacity-50 font-medium"
          >
            {comparing ? 'Comparing...' : 'Compare to Previous Period'}
          </button>
        </div>
      )}

      {/* Electricity Meter Consumption */}
      {elecMeters.length > 0 && (
        <Card className="p-6">
          <h3 className="text-sm font-semibold text-content mb-4">Electricity Meter Consumption</h3>
          <DataTable
            data={elecMeters}
            columns={elecColumns}
            rowKey={(m) => m._key}
            density="compact"
          />
          {/* DataTable has no footer-row slot, so the aggregate row that used
              to be a <tfoot>-style <tr> is a summary strip below the table
              instead — same values (Max kVA stays a Math.max, not a sum). */}
          <TotalsStrip>
            <div className="font-semibold text-content">Total</div>
            <TotalsItem label="kWh" value={fmtNum(elecMeters.reduce((s, m) => s + m.delta_kwh, 0), 1)} />
            {data.comparison_available && (
              <TotalsItem label="Prev kWh" value={fmtNum(elecMeters.reduce((s, m) => s + (m.prev_delta || 0), 0), 1)} />
            )}
            <TotalsItem label="Max kVA" value={fmtNum(Math.max(...elecMeters.map(m => m.max_kva), 0), 1)} />
            <TotalsItem label="CA kWh" value={fmtNum(elecMeters.reduce((s, m) => s + m.common_area_kwh, 0), 1)} />
            <div className="ml-auto font-semibold text-content font-mono">{fmtCurrency(elecMeters.reduce((s, m) => s + m.total_amount_cents, 0))}</div>
          </TotalsStrip>
        </Card>
      )}

      {/* Water Meter Consumption */}
      {waterMeters.length > 0 && (
        <Card className="p-6">
          <h3 className="text-sm font-semibold text-content mb-4">Water Meter Consumption</h3>
          <DataTable
            data={waterMeters}
            columns={waterColumns}
            rowKey={(m) => m._key}
            density="compact"
          />
          <TotalsStrip>
            <div className="font-semibold text-content">Total</div>
            <TotalsItem label="kL" value={fmtNum(waterMeters.reduce((s, m) => s + m.delta_kl, 0), 1)} />
            {data.comparison_available && (
              <TotalsItem label="Prev kL" value={fmtNum(waterMeters.reduce((s, m) => s + (m.prev_delta || 0), 0), 1)} />
            )}
            <div className="ml-auto font-semibold text-content font-mono">{fmtCurrency(waterMeters.reduce((s, m) => s + m.total_amount_cents, 0))}</div>
          </TotalsStrip>
        </Card>
      )}

      {/* Charts */}
      {(barData.length > 0 || pieData.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Bar Chart — Consumption per meter */}
          {barData.length > 0 && (
            <Card className="p-6">
              <h3 className="text-sm font-semibold text-content mb-4">
                Consumption by Meter (kWh)
              </h3>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={barData} margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-30} textAnchor="end" height={60} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip formatter={(v) => `${fmtNum(Number(v))} kWh`} />
                  <Bar dataKey="current" fill="#3B82F6" name="Current" radius={[4, 4, 0, 0]} />
                  {data.comparison_available && (
                    <Bar dataKey="previous" fill="#CBD5E1" name="Previous" radius={[4, 4, 0, 0]} />
                  )}
                </BarChart>
              </ResponsiveContainer>
            </Card>
          )}

          {/* Charge breakdown — donut at xl+, labelled list below it.
              WHY: recharts sizes a Pie against the plot box that is LEFT OVER
              after the legend is subtracted. selectChartOffsetInternal calls
              appendOffsetOfLegend, which for the default horizontal/bottom
              legend does `offset.bottom += legendBox.height`; Pie's
              parseCoordinateOfPie then resolves cy from that reduced
              offset.height (cy = top + height/2). outerRadius={90} is a FIXED
              pixel value, so the donut does NOT shrink with the box. With
              height={250} and margin 5: cy = 5 + (240 - L)/2, so the donut's
              top edge is at (240 - L)/2 - 85 and goes negative once the legend
              measures taller than L = 70px. That single fact produces BOTH
              reported symptoms — the top is clipped, and the bottom of the
              donut runs down into the legend band that starts at 250 - L.
              A nine-charge legend at fontSize 11 wraps well past 70px in any
              card narrower than ~440px. Card width here is 263px at a 375px
              viewport (375 - 64 page p-8 - 48 Card p-6) and 292px at lg
              (1024 - 256 aside w-64 - 64 - 24 gap, halved by lg:grid-cols-2,
              - 48) — lg is therefore NOT a safe floor, the two-column grid
              lands at exactly the width that breaks. xl is: 420px at 1280,
              500px at 1440 (the width the user confirmed reads correctly).
              At >= xl the donut renders with exactly the props it always had,
              so 1440 is unchanged. */}
          {pieData.length > 0 && (
            <Card className="p-6">
              <h3 className="text-sm font-semibold text-content mb-4">
                Charge Breakdown
              </h3>
              {isWideChart ? (
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie data={pieData} cx="50%" cy="50%" innerRadius={50} outerRadius={90} paddingAngle={2} dataKey="value">
                      {pieData.map((_, i) => (
                        <Cell key={i} fill={COLORS[i % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v) => `R ${fmtNum(Number(v), 2)}`} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                /* Same pieData, same per-charge series colour, same currency
                   string the donut's own Tooltip formats (`R ${fmtNum(v, 2)}`)
                   — presentation only, no new or reshaped data. The share bar
                   carries the proportion the slice angle used to; naming every
                   row also removes the donut's 9-charges-into-8-COLORS
                   ambiguity (i % length paints the 9th slice the same blue as
                   the 1st, which the legend cannot disambiguate).
                   Colour is redundant here — each row is identified by
                   text-content (17.49:1 light / 14.52:1 dark on the Card's
                   surface-raised) — so the series hues stay exactly as the
                   donut has them even though 4 of the 8 sit under 3:1 against
                   #ffffff (emerald 2.54, amber 2.15, cyan 2.43, lime 1.98).
                   Restyling them would change the desktop donut, which is out
                   of scope. text-content-muted is 4.80:1 light / 6.01:1 dark
                   on the same surface — both AA, both already policed by
                   test/contrast.test.ts's textMuted/surfaceRaised pair. */
                <ul className="space-y-2.5">
                  {pieData.map((d, i) => {
                    const pct = pieTotal > 0 ? (d.value / pieTotal) * 100 : 0;
                    const color = COLORS[i % COLORS.length];
                    return (
                      <li key={`${d.name}-${i}`} className="flex gap-2">
                        <span
                          aria-hidden="true"
                          className="mt-1.5 h-2.5 w-2.5 shrink-0 rounded-sm"
                          style={{ backgroundColor: color }}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-baseline justify-between gap-2">
                            <span className="text-sm text-content">{d.name}</span>
                            <span className="shrink-0 font-mono text-sm text-content">R {fmtNum(d.value, 2)}</span>
                          </div>
                          <div className="mt-1 flex items-center gap-2">
                            <div className="h-1 flex-1 overflow-hidden rounded-full bg-surface-sunken">
                              <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: color }} />
                            </div>
                            <span className="shrink-0 font-mono text-xs text-content-muted">{fmtNum(pct, 1)}%</span>
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
